import { use, useEffect, useState } from 'react';
import { Path, useForm, UseFormReturn, useFormState } from 'react-hook-form';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';
import defaultValues from 'shared/constants/defaultValues';

import WanderingBoundaryPassage from 'features/wandering-boundary-settings/domain/models/wandering-boundary-passage';
import WanderingBoundarySettingsProviderContext from 'features/wandering-boundary-settings/providers/contexts/wandering-boundary-provider-context';

export type WanderingBoundaryPassageRowProviderState = {
  wanderingBoundaryPassage: WanderingBoundaryPassage;
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
  form: UseFormReturn<WanderingBoundaryPassage>;
  submitForm: () => void;
  viewingMode: ViewingMode;
  setViewingMode: (viewingMode: ViewingMode) => void;
  startEditing: () => void;
  stopEditing: () => void;
  cancelCreateWanderingBoundaryPassage: (wanderingBoundaryPassage: WanderingBoundaryPassage) => void;
  handleBlockingPassageWarningCheckboxChange: (timeFieldName: Path<WanderingBoundaryPassage>, checked: boolean) => void;
};

export const BlockingPassageWarningTimeConstants = {
  min: 1,
  max: 10,
};

const useWanderingBoundaryPassageRowProviderState = (
  wanderingBoundaryPassage: WanderingBoundaryPassage,
  initialViewingMode: ViewingMode,
): WanderingBoundaryPassageRowProviderState => {
  const { detailsState } = use(WanderingBoundarySettingsProviderContext);
  const { enablePopUpGuard } = use(ConfirmationPopupProviderContext);
  const {
    createWanderingBoundaryPassage,
    updateWanderingBoundaryPassage,
    removeWanderingBoundaryPassage,
    setIsEditing,
    setShouldShowConfirmationPopup,
  } = detailsState;

  const [isExpanded, setIsExpanded] = useState(false);
  const [viewingMode, setViewingMode] = useState(initialViewingMode);

  const form = useForm<WanderingBoundaryPassage>({
    mode: 'onBlur',
    defaultValues: wanderingBoundaryPassage,
  });

  const { isDirty } = useFormState({ control: form.control });

  const submitForm = (): void => {
    const formData = form.getValues();

    formData.domipADeviceId = formData.domipADeviceId === defaultValues.defaultSelectValue ? null : formData.domipADeviceId;
    formData.domipBDeviceId = formData.domipBDeviceId === defaultValues.defaultSelectValue ? null : formData.domipBDeviceId;

    if (formData.id) {
      updateWanderingBoundaryPassage(formData, handleUpdateError);
    } else {
      createWanderingBoundaryPassage(formData, handleCreateError);
    }

    setShouldShowConfirmationPopup(false);
    enablePopUpGuard(false);
    setIsExpanded(true);
    setViewingMode('viewing');
  };

  const startEditing = (): void => {
    setIsEditing(true);
    setViewingMode('editing');
  };

  const handleCreateError = (): void => {
    setViewingMode('creation');
  };

  const handleUpdateError = (): void => {
    setViewingMode('editing');
  };

  const stopEditing = (): void => {
    setIsEditing(false);
    setViewingMode('viewing');
    form.reset(wanderingBoundaryPassage);
  };

  const cancelCreateWanderingBoundaryPassage = (wanderingBoundaryPassage: WanderingBoundaryPassage): void => {
    setIsEditing(false);
    setShouldShowConfirmationPopup(false);
    enablePopUpGuard(false);
    form.reset(wanderingBoundaryPassage);
    removeWanderingBoundaryPassage(wanderingBoundaryPassage);
  };

  const handleBlockingPassageWarningCheckboxChange = (timeFieldName: Path<WanderingBoundaryPassage>, checked: boolean): void => {
    if (checked) {
      form.setValue(timeFieldName, BlockingPassageWarningTimeConstants.min, { shouldDirty: true });
    } else {
      form.setValue(timeFieldName, null, { shouldDirty: true });
    }
  };

  useEffect(() => {
    setShouldShowConfirmationPopup(isDirty);
    enablePopUpGuard(isDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  useEffect(() => {
    form.reset(wanderingBoundaryPassage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanderingBoundaryPassage.id]);

  return {
    wanderingBoundaryPassage,
    isExpanded,
    setIsExpanded,
    form,
    submitForm,
    viewingMode,
    setViewingMode,
    startEditing,
    stopEditing,
    cancelCreateWanderingBoundaryPassage,
    handleBlockingPassageWarningCheckboxChange,
  };
};

export default useWanderingBoundaryPassageRowProviderState;
